import BaseImage from "@components/BaseImage";
import AVS_CMS_DATA from "@data/avs.json";
import { useRestakeApy } from "@hooks/useRestakeApy";
import useTVL from "@hooks/useTVL";
import { Card, Search, Typography } from "@kelp-dao/kernel-design-system";
import { useState } from "react";
import { numberToCurrency } from "utils";

type TAvsCardData = {
  address: string;
  title: string;
  desc: string;
  imgUrl: string;
  amtDel: string | number;
  rewards: string;
};

const AVSV2 = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const tvl = useTVL();
  const { roundedTotalAPY } = useRestakeApy();

  const avsData: TAvsCardData[] = AVS_CMS_DATA.avs_data.map((item) => ({
    address: item.address,
    title: item.avs_details.title,
    desc: item.avs_details.description,
    imgUrl: item.avs_details.img_key,
    amtDel: 0,
    rewards: isNaN(Number(item.rewards))
      ? item.rewards
      : numberToCurrency(item.rewards),
  }));

  const filteredData = avsData.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayData = searchQuery ? filteredData : avsData;

  return (
    <div className="flex flex-col gap-6 justify-center items-center w-full">
      <div className="flex flex-col gap-6 justify-between items-center w-full mb-20">
        <div className="flex flex-col md:flex-row justify-start md:justify-between items-start md:items-center w-full gap-4">
          <Typography
            as="h1"
            type="heading"
            variant="default"
            className="text-xl font-medium md:text-2xl md:font-semibold"
          >
            Actively validated services
          </Typography>
          <div className="flex gap-6 md:gap-10 justify-start md:justify-end items-start w-full md:w-max">
            <div className="flex flex-col gap-0.5 w-max">
              <Typography
                type="misc"
                as="p"
                variant="overline"
                className="text-text-secondary"
              >
                Total value locked
              </Typography>
              <Typography
                type="heading"
                variant="xxsmall"
                className="text-text-primary"
              >
                {tvl.tvl?.value
                  ? numberToCurrency(`${tvl.tvl.value}`) + " ETH"
                  : "-"}
              </Typography>
              <Typography
                type="misc"
                as="p"
                variant="label"
                className="text-text-secondary"
              >
                $1.49 Bn
              </Typography>
            </div>
            <div className="flex flex-col gap-0.5">
              <Typography
                type="misc"
                as="p"
                variant="overline"
                className="text-text-secondary"
              >
                APY
              </Typography>
              <Typography
                type="heading"
                variant="xxsmall"
                className="text-text-primary"
              >
                {roundedTotalAPY}
              </Typography>
            </div>
            <div className="flex-grow flex md:hidden"></div>
            <Search
              placeholder="Search"
              onSearch={setSearchQuery}
              className="max-w-[138px]"
              value={searchQuery}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 w-full">
          {displayData.map((item, index) => (
            <Card
              key={`${item.title}-${index}`}
              className="px-4 py-5 flex gap-2 justify-center items-start"
              variant="non-bordered"
            >
              <BaseImage
                src={item.imgUrl}
                alt={item.title}
                width={24}
                height={24}
              />
              <div className="flex-grow flex flex-col gap-4 h-full">
                <Typography
                  type="heading"
                  variant="xxsmall"
                  className="text-text-primary"
                >
                  {item.title}
                </Typography>
                <Typography
                  type="body"
                  variant="xsmall"
                  className="text-text-secondary"
                >
                  {item.desc}
                </Typography>
                <div className="flex-grow flex justify-center items-end">
                  <hr className="w-full border-t border-border-default" />
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex justify-between items-center">
                    <Typography
                      type="body"
                      variant="xsmall"
                      className="text-text-secondary"
                    >
                      Amount Delegated
                    </Typography>
                    <Typography
                      type="body"
                      variant="xsmall"
                      className="text-text-primary"
                    >
                      {item.amtDel} ETH
                    </Typography>
                  </div>
                  <div className="flex justify-between items-center">
                    <Typography
                      type="body"
                      variant="xsmall"
                      className="text-text-secondary"
                    >
                      Rewards
                    </Typography>
                    <Typography
                      type="body"
                      variant="xsmall"
                      className="text-text-primary"
                    >
                      {item.rewards}
                    </Typography>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AVSV2;
